import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Calendar, ShieldCheck, Award, Camera } from 'lucide-react';
import Avatar from '../components/ui/Avatar';
import TextInput from '../components/ui/TextInput';
import Button from '../components/ui/Button';
import GenericFormDrawer from '../components/GenericFormDrawer';
import PasswordPolicyChecklist from '../components/PasswordPolicyChecklist';
import MfaSecuritySection from '../components/mfa/MfaSecuritySection';
import { useAuth } from '../context/AuthContext';
import { getProfile, changePassword, getMyPasswordPolicy, updateMyProfilePhoto } from '../services/profileService';
import { getMe } from '../services/authService';
import { DEFAULT_PASSWORD_POLICY, isPasswordAllowed, PASSWORD_EXPIRY_WARNING_DAYS } from '../utils/passwordPolicy';
import { presignAndUploadFile } from '../services/storageService';
import { formatDate as formatDateUtil } from '../utils/Utils';
import { formatPhone } from '../utils/phone';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1200px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 24px 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 20px;
`;

const FullWidthCard = styled(Card)`
  grid-column: 1 / -1;
`;

const AvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ChangePhotoButton = styled.button`
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: ${({ theme }) => theme.colors.primary.main};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

/* Card 1 — User Info */
const UserInfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserName = styled.span`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: #004242;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const UserTitles = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #A0A8B4;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const TenantBadge = styled.span`
  display: inline-block;
  margin-top: 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.primary.main};
  background: rgba(2, 145, 239, 0.08);
  padding: 4px 10px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

/* Card 2 — Account Security */
const SecurityContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: #004242;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const SecurityDesc = styled.p`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #A0A8B4;
  margin: 0;
  line-height: 1.5;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

/* Card 3 — Personal Info */
const InfoRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBox = styled.div`
  width: 40px;
  height: 40px;
  background: #F5F7FA;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: #A0A8B4;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const InfoValue = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #004242;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

/* Card 4 — Certifications */
const CertHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ViewAllLink = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #A0A8B4;
  cursor: not-allowed;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const EmptyState = styled.p`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #A0A8B4;
  margin: 16px 0 0 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

/* Şifre değiştirme çekmecesi */
const DrawerFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Şifre politikası: şifrenin süresinin dolmasına kalan gün. Son günlerde uyarı rengi.
const ExpiryNote = styled.p`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ $warn, theme }) => ($warn ? theme.colors.auxiliar.danger : '#6B7280')};
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.auxiliar?.danger || '#e53e3e'};
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const EMPTY_PASSWORDS = { currentPassword: '', newPassword: '', confirmPassword: '' };

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [passwords, setPasswords] = useState(EMPTY_PASSWORDS);
  const [formError, setFormError] = useState('');
  // Kişinin şirketinin şifre kuralı (kontrol listesi). Sunucu kayıtta aynı kuralı yeniden denetler.
  const [policy, setPolicy] = useState(DEFAULT_PASSWORD_POLICY);

  const { user } = useAuth();
  // Zorunlu SSO + oturum SSO ile açılmış (spec §5.5): parola kurumun IdP'sinde. Acil durum yöneticisinin parola oturumunda
  // Parola Değiştir ve süre uyarısı görünür kalır.
  const ssoManaged = user?.ssoEnforced === true && user?.authMethod === 'sso';
  const canChangePhoto = user?.canChangeOwnPhoto !== false;
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(false);

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingPhoto(true);
    setPhotoError(false);
    try {
      const fileUrl = await presignAndUploadFile(file, 'avatar');
      await updateMyProfilePhoto(fileUrl);
      setProfile((prev) => (prev ? { ...prev, profilePicture: fileUrl } : prev));
      // Header avatarı AuthContext'ten okur; mevcut auth:refreshed kanalıyla tazele.
      if (user) {
        window.dispatchEvent(new CustomEvent('auth:refreshed', {
          detail: { ...user, profilePicture: fileUrl },
        }));
      }
    } catch (err) {
      // Presign/kayıt çağrıları api üzerinden gider ve interceptor mesajını basar;
      // yalnız ham R2 adımının hatası (isRawUploadError) sayfada gösterilir.
      if (err?.isRawUploadError) setPhotoError(true);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await getProfile();
      setProfile(res.data?.data || res.data);
    } catch {
      // Error handled by interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const openPasswordDrawer = useCallback(() => {
    setPasswords(EMPTY_PASSWORDS);
    setFormError('');
    setDrawerOpen(true);
    getMyPasswordPolicy().then((res) => {
      if (res?.data?.data) setPolicy(res.data.data);
    });
  }, []);

  // Yaklaşan süre dolumu bandı buraya `?changePassword=1` ile yönlendirir: çekmece açık gelsin.
  // (Bant /profile'da gösterilmez; bu yüzden yalnız ilk yükte okumak yeterli.)
  useEffect(() => {
    if (!ssoManaged && new URLSearchParams(window.location.search).get('changePassword') === '1') openPasswordDrawer();
  }, [openPasswordDrawer, ssoManaged]);

  const handlePasswordChange = (field) => (e) => {
    setPasswords((prev) => ({ ...prev, [field]: e.target.value }));
    setFormError('');
  };

  // GenericFormDrawer KABUK sözleşmesi: false → çekmece açık kalır; fırlatılan hata (sunucu
  // reddi) da çekmeceyi açık tutar ve mesajı interceptor gösterir.
  const handleSubmitPassword = async () => {
    if (!passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword) {
      setFormError(t('common.fillRequired'));
      return false;
    }
    if (!isPasswordAllowed(passwords.newPassword, policy)) {
      setFormError(t('passwordPolicy.notMet'));
      return false;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      setFormError(t('profile.passwordMismatch'));
      return false;
    }

    await changePassword(passwords);
    setPasswords(EMPTY_PASSWORDS);
    // Kalan gün sayacı (bant + bu kart) yeni şifreyle yeniden hesaplansın.
    getMe().then((res) => {
      const fresh = res?.data?.data;
      if (fresh) window.dispatchEvent(new CustomEvent('auth:refreshed', { detail: fresh }));
    });
    return true;
  };

  const expiresInDays = user?.passwordExpiresInDays;

  const formatDate = (dateStr) => formatDateUtil(dateStr, i18n.language);

  if (loading) return null;
  if (!profile) return null;

  const titlesText = profile.titles?.length > 0
    ? profile.titles.map((t) => t.name).join(', ')
    : t('profile.noTitles');

  return (
    <PageContainer>
      <PageTitle>{t('profile.title')}</PageTitle>
      <Grid>
        {/* Card 1 — User Info */}
        <Card>
          <UserInfoHeader>
            <AvatarWrap>
              <Avatar
                src={profile.profilePicture}
                firstName={profile.firstName || '?'}
                lastName={profile.lastName || '?'}
                size={80}
              />
              {canChangePhoto && (
                <>
                  <ChangePhotoButton
                    type="button"
                    title={t('profile.changePhoto')}
                    aria-label={t('profile.changePhoto')}
                    disabled={uploadingPhoto}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera size={14} />
                  </ChangePhotoButton>
                  <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelected}
                  />
                </>
              )}
            </AvatarWrap>
            <UserDetails>
              <UserName>{profile.fullName || '-'}</UserName>
              <UserTitles>{titlesText}</UserTitles>
              {profile.tenantName && <TenantBadge>{profile.tenantName}</TenantBadge>}
              {photoError && <ErrorText>{t('profile.photoUploadFailed')}</ErrorText>}
            </UserDetails>
          </UserInfoHeader>
        </Card>

        {/* Card 2 — Account Security */}
        <Card>
          <SecurityContent>
            <CardTitle>
              <ShieldCheck size={20} />
              {t('profile.accountSecurity')}
            </CardTitle>
            <SecurityDesc>{t('profile.securityDesc')}</SecurityDesc>
            {ssoManaged ? (
              <SecurityDesc data-testid="sso-managed-note">{t('profile.ssoManaged')}</SecurityDesc>
            ) : (
              <>
                {expiresInDays != null && (
                  <ExpiryNote $warn={expiresInDays <= PASSWORD_EXPIRY_WARNING_DAYS} data-testid="password-expiry-note">
                    {t('profile.passwordExpiresIn', { count: expiresInDays })}
                  </ExpiryNote>
                )}
                <div>
                  <Button onClick={openPasswordDrawer}>
                    <ShieldCheck size={16} />
                    {t('profile.changePassword')}
                  </Button>
                </div>
              </>
            )}
            <MfaSecuritySection />
          </SecurityContent>
        </Card>

        {/* Card 3 — Personal Info */}
        <FullWidthCard>
          <CardTitle>{t('profile.personalInfo')}</CardTitle>
          <InfoRows>
            <InfoRow>
              <IconBox><Mail size={18} color="#0291EF" /></IconBox>
              <InfoContent>
                <InfoLabel>{t('profile.email')}</InfoLabel>
                <InfoValue>{profile.corporateEmail || profile.email || '-'}</InfoValue>
              </InfoContent>
            </InfoRow>
            <InfoRow>
              <IconBox><Phone size={18} color="#0291EF" /></IconBox>
              <InfoContent>
                <InfoLabel>{t('profile.phone')}</InfoLabel>
                <InfoValue>{formatPhone(profile.areaCode, profile.phoneNumber)}</InfoValue>
              </InfoContent>
            </InfoRow>
            <InfoRow>
              <IconBox><MapPin size={18} color="#0291EF" /></IconBox>
              <InfoContent>
                <InfoLabel>{t('profile.location')}</InfoLabel>
                <InfoValue>{profile.tz || '-'}</InfoValue>
              </InfoContent>
            </InfoRow>
            <InfoRow>
              <IconBox><Calendar size={18} color="#0291EF" /></IconBox>
              <InfoContent>
                <InfoLabel>{t('profile.employmentDate')}</InfoLabel>
                <InfoValue>{formatDate(profile.employmentDate)}</InfoValue>
              </InfoContent>
            </InfoRow>
          </InfoRows>
        </FullWidthCard>

        {/* Card 4 — Certifications */}
        <FullWidthCard>
          <CertHeader>
            <CardTitle>
              <Award size={20} />
              {t('profile.certifications')}
            </CardTitle>
            <ViewAllLink>{t('profile.viewAll')}</ViewAllLink>
          </CertHeader>
          <EmptyState>{t('profile.noCertifications')}</EmptyState>
        </FullWidthCard>
      </Grid>

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('profile.changePassword')}
        schema={[]}
        onSave={handleSubmitPassword}
      >
        <DrawerFields>
          <TextInput
            label={t('profile.currentPassword')}
            type="password"
            value={passwords.currentPassword}
            onChange={handlePasswordChange('currentPassword')}
            required
          />
          <TextInput
            label={t('profile.newPassword')}
            type="password"
            value={passwords.newPassword}
            onChange={handlePasswordChange('newPassword')}
            required
          />
          <TextInput
            label={t('profile.confirmPassword')}
            type="password"
            value={passwords.confirmPassword}
            onChange={handlePasswordChange('confirmPassword')}
            required
            error={formError}
          />
          <PasswordPolicyChecklist password={passwords.newPassword} policy={policy} />
        </DrawerFields>
      </GenericFormDrawer>
    </PageContainer>
  );
}
